import { Button } from '@/components/ui/button';
import { useCookieConsentStore } from '@/store/cookie-consent-store';

/**
 * Cookie notice (PP-027).
 *
 * The body is the Business/Legal-approved copy and is reproduced verbatim — do not rephrase it. The
 * ticket pasted the two destinations as bare URLs inside the sentence ("please review our
 * <url> or visit our <url> page"), which reads as link text having been lost in the paste; they are
 * rendered here as links in the positions those URLs occupied, with the sentence otherwise untouched.
 * Confirm the link wording with Legal.
 *
 * Mounted twice — the shell and the login screen, which renders outside it — so acceptance lives in a
 * persisted store rather than in component state (PP-027: once accepted it does not come back).
 */
const PRIVACY_POLICY_URL = 'https://bankersfidelity.com/privacypolicy/';
const DO_NOT_SELL_URL = 'https://bankersfidelity.com/do-not-sell/';

const NOTICE_BODY =
  'We use cookies and similar technologies to enhance your browsing experience, analyze site usage, ' +
  'and support our marketing efforts. This helps us better understand how you interact with our site ' +
  'and improve the products and services we offer. We may share limited data with trusted partners in ' +
  'accordance with our privacy practices.';

export function CookieNotice(): React.JSX.Element | null {
  const accepted = useCookieConsentStore((state) => state.accepted);
  const accept = useCookieConsentStore((state) => state.accept);

  if (accepted) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      // Fixed to the bottom and above the chat trigger. It never covers the page content it sits over
      // for long, and nothing behind it is disabled: PP-027 AC-006 requires the notice not to block
      // access to portal functionality.
      className="border-line fixed inset-x-0 bottom-0 z-50 border-t bg-surface shadow-[0_-8px_24px_-18px_rgba(20,53,94,.6)]"
    >
      <div className="mx-auto flex max-w-shell flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:gap-6 sm:px-10">
        <p className="text-ink flex-1 text-[13px]">
          {NOTICE_BODY}{' '}
          For more information, please review our{' '}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer"
            className="text-brand-500 underline"
          >
            Privacy Policy
            <span className="sr-only"> (opens in a new tab)</span>
          </a>{' '}
          or visit our{' '}
          <a
            href={DO_NOT_SELL_URL}
            target="_blank"
            rel="noreferrer"
            className="text-brand-500 underline"
          >
            Do Not Sell
            <span className="sr-only"> (opens in a new tab)</span>
          </a>{' '}
          page.
        </p>

        <Button type="button" size="sm" onClick={accept} className="flex-none self-start sm:self-auto">
          Accept
        </Button>
      </div>
    </div>
  );
}
